<template>
  <div id="app">
    <v-app :style="{ background: $vuetify.theme.themes[theme].background }">
      <ProgressBar />
      <template v-if="isLoggedIn">
        <NavDrawer
          :drawer-open="appDrawerOpen"
          @update-drawer="updateDrawerStatus"
        />
        <NavBar @toggle-app-drawer="appDrawerOpen = !appDrawerOpen" />
      </template>
      <transition
        name="ill-app-fade"
        mode="out-in"
      >
        <v-main class="mt-12">
          <router-view @display-alert="callDisplayAlert" />
        </v-main>
      </transition>
      <RecordEditorCreateDialog v-if="createRecordDialogOpen" />
      <LastLoginInfoDialog />
      <AlertBox v-if="alert" />
      <DownloadNotifications />
      <EntriesMassAssignmentNotifications />
      <v-dialog
        v-model="showWarning"
        persistent
        width="522"
      >
        <v-card id="inactivity-warning">
          <v-card-title primary-title>
            <v-icon
              color="white"
              class="mr-2"
            >
              notification_important
            </v-icon>Warning
          </v-card-title>

          <v-container>
            <p>You will be logged out in {{ refreshTokenExpiresAtWarnLimit }}.</p>
          </v-container>

          <v-card-actions>
            <v-spacer />
            <v-btn
              color="primary"
              @click="keepActive"
            >
              Remain active
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <PEQuickEdit v-if="peSharedQESliderStatus" />
      <DocketEntryEditorDialog v-if="isDocketEntryEditorVisible" />
    </v-app>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { alertHandler } from '@/alertHandler'
import { createRgbVarsForThemes } from '@/utilities/hex-to-rgba'

import { DOCKET_ENTRY_NAMESPACE } from '@/store/docket-entry'
import { PARTY_NAMESPACE } from '@/store/party'

import AlertBox from '@/components/Misc/AlertBox.vue'
import DocketEntryEditorDialog from '@/components/DocketEntry/Editor/DocketEntryEditorDialog.vue'
import DownloadNotifications from '@/components/Downloads/DownloadNotifications.vue'
import EntriesMassAssignmentNotifications from '@/components/DocketEntry/EntriesMassAssignmentNotifications.vue'
import LastLoginInfoDialog from './components/Dialogs/LastLoginInfoDialog.vue'
import NavBar from '@/components/Nav/Bar.vue'
import NavDrawer from '@/components/Nav/Drawer.vue'
import PEQuickEdit from '@/components/Party/PartyEdit/PEQuickEdit.vue'
import ProgressBar from '@/components/Nav/ProgressBar.vue'
import RecordEditorCreateDialog from '@/components/Record/Editor/RecordEditorCreateDialog.vue'
import moment from 'moment-timezone'

export default {
  name: 'App',

  components: {
    AlertBox,
    DocketEntryEditorDialog,
    DownloadNotifications,
    EntriesMassAssignmentNotifications,
    LastLoginInfoDialog,
    NavBar,
    NavDrawer,
    PEQuickEdit,
    ProgressBar,
    RecordEditorCreateDialog,
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      appDrawerOpen: false,
      expiryTimers: [],
      notifications: {
        result: [],
      },

      showWarning: false,
      warnCountTimers: [],
      warningTimers: [],
    }
  },

  computed: {
    theme () {
      return (this.$vuetify.theme.dark) ? 'dark' : 'light'
    },

    ...mapGetters([
      'alert',
      'createRecordDialogOpen',
      'isLoggedIn',
      'refreshTokenExpiresAt',
      'refreshTokenExpiresAtWarnLimit',
    ]),

    ...mapGetters(DOCKET_ENTRY_NAMESPACE, [
      'isDocketEntryEditorVisible',
    ]),

    ...mapGetters(PARTY_NAMESPACE, [
      'peSharedQESliderStatus',
    ]),
  },

  watch: {
    '$vuetify.theme.dark': {
      immediate: true,

      handler: function (val) {
        if (val) {
          createRgbVarsForThemes(this.$vuetify.theme.themes.dark)
        } else {
          createRgbVarsForThemes(this.$vuetify.theme.themes.light)
        }
      },
    },

    isLoggedIn (val) {
      if (val) this.monitorActivity()
      else this.cleanupMonitoring()
    },
  },

  created () {
    window.addEventListener('storage', this.syncStatus)
    if (this.isLoggedIn) this.monitorActivity()
  },

  methods: {
    callDisplayAlert (passedAlert) {
      this.displayAlert(passedAlert.type,
        passedAlert.message ? passedAlert.message : null,
        passedAlert.error ? passedAlert.error : null)
    },

    async cleanupMonitoring () {
      this.showWarning = false
      this.setRefreshTokenExpiresAtWarnLimit(0)

      await this.clearExpiryTimers()
      await this.clearWarningTimers()
    },

    clearExpiryTimers () {
      this.expiryTimers.forEach(expiryTimer => {
        clearTimeout(expiryTimer)
      })
      this.expiryTimers = []
    },

    clearWarningTimers () {
      this.warningTimers.forEach(warningTimer => {
        clearTimeout(warningTimer)
      })
      this.warningTimers = []

      this.warnCountTimers.forEach(warnCountTimer => {
        clearInterval(warnCountTimer)
      })
      this.warnCountTimers = []
    },

    async keepActive () {
      window.localStorage.setItem('active', Date.now())

      await this.refreshUserModel()
      await this.monitorActivity()
    },

    async logout (fromOtherTab) {
      this.cleanupMonitoring()
      this.$store.dispatch('logoutUserModel').then(() => {
        if (!fromOtherTab) window.localStorage.setItem('logout', Date.now())
        if (this.$route.name !== 'login') this.$router.push('/login')
      })
    },

    async monitorActivity () {
      await this.cleanupMonitoring()
      this.netRefreshTokenExpiresAt()
      this.startTimer()
    },

    netRefreshTokenExpiresAt () {
      this.refreshTokenWarnAt = moment(this.refreshTokenExpiresAt).subtract(2, 'minutes')
    },

    refreshUser () {
      this.refreshUserModel().then(_ => {
        const path = window.sessionStorage.getItem('returnTo')
        // note: just catching here to avoid error in browser console. could do on all $router.push()
        if (path && this.$route.path !== path) this.$router.push(path).catch(_ => {})
        else if (this.$route.path !== '/') this.$router.push('/')
      })
    },

    startTimer () {
      const now = moment()
      const expiryLimit = this.refreshTokenWarnAt.diff(now, 'milliseconds', true)

      // call this.timeout if expiryLimit is reached
      const expiryTimer = window.setTimeout(this.startWarning, expiryLimit)
      this.expiryTimers.push(expiryTimer)
    },

    async startWarning () {
      this.clearExpiryTimers()
      this.showWarning = true

      const refreshTokenExpireAt = moment(this.refreshTokenExpiresAt).subtract(1, 'minutes')
      const now = moment()
      const expiryLimit = refreshTokenExpireAt.diff(now, 'milliseconds', true)

      // call this.logout if expiryLimit is reached
      const warningTimer = window.setTimeout(this.logout, expiryLimit)
      this.warningTimers.push(warningTimer)
      this.warnCounter(expiryLimit)
    },

    syncStatus () {
      if (event.key === 'logout') {
        // logged out from another tab
        const fromOtherTab = true
        this.logout(fromOtherTab)
      }

      // another tab successfully authenticated, so we'll restore window
      if (event.key === 'login') this.refreshUser()

      // close warning if another tab signaled activity
      if (event.key === 'active') this.showWarning = false
    },

    updateDrawerStatus (value) {
      this.appDrawerOpen = value
    },

    warnCounter (expiryLimit) {
      const dis = this
      let duration = moment.duration(expiryLimit)
      const warnCountTimer = setInterval(_ => {
        duration = moment.duration(duration - 1000, 'milliseconds')
        if (duration.asSeconds() <= 0) {
          dis.cleanupMonitoring()
          dis.setRefreshTokenExpiresAtWarnLimit(0)
        } else {
          const time = duration.minutes() + ' minutes ' + duration.seconds() + ' seconds'
          dis.setRefreshTokenExpiresAtWarnLimit(time)
        }
      }, 1000)

      this.warnCountTimers.push(warnCountTimer)
    },

    ...mapActions([
      'refreshUserModel',
    ]),

    ...mapMutations([
      'setRefreshTokenExpiresAtWarnLimit',
    ]),
  },
}
</script>
